import { useEffect, useMemo, useRef } from 'react';
import { hashKey, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useShellSearchParams } from '../../lib/use-shell-search-params';
import { countryConfig } from '../../country';
import { track } from '../../lib/funnel';
import { Seo } from '../../components/seo';
import { TenderList } from '../../components/tender-list';
import { Pagination } from '../../components/pagination';
import { UpgradeBanner } from '../../components/upgrade-banner';
import { ChannelSubscribeCta } from '../../components/channel-subscribe-cta';
import { OnboardingBanner } from '../../components/onboarding-banner';
import { QueryError, SlowLoadNotice, TenderListSkeleton } from '../../components/query-error';
import { ResultsSearchCard } from '../../components/results-search-card';
import { downloadResultsExport, fetchAwardedSummary, fetchCatalog } from '../../lib/catalog-client';
import { ExportButton } from '../../components/export-button';
import { getResultsSearch, listSavedSearches } from '../../lib/saved-search-client';
import {
  fromResultsCriteria,
  hasResultsState,
  parseResultsParams,
  RESULTS_SORT,
  RESULTS_WINDOW_DAYS,
  resultsCriteriaParam,
  writeResultsParams,
  type ResultsCardState,
  type ResultsCriteria,
} from '../../lib/results-search';
import { useAuth } from '../../lib/auth-context';
import { isPaidUser } from '../../lib/user-display';
import { useListDensity } from '../../lib/list-density';
import { usePageTitle } from '../../lib/page-title';
import { useLocale } from '../../i18n/locale-context';
import { useLocaleNavigate } from '../../i18n/locale-link';
import { localePath } from '../../i18n/locale-path';
import { tenderPath } from '../../lib/slug';
import { listingPageHref, resolveListingPage } from '../../lib/pagination-paths';
import { catalogListKey } from './catalog';

const SITE_ORIGIN = countryConfig.brand.siteUrl;
const PAGE_SIZE = 20;

/**
 * `/results` — the awarded archive, searched with ONE card (owner, 2026-10-07).
 *
 * What the plain catalogue shows here — the topbar semantic search, the kind
 * tabs, the filter bar — is replaced by «Что вы поставляете?»
 * (`ResultsSearchCard`): word and category/ИКПУ chips, a collapsed «Уточнить»
 * panel, the count line and «Сохранить поиск». The card's state lives in the
 * URL (`lib/results-search.ts`) and the list below is `GET /catalog` over the
 * archive (`status=closed&awarded=true`) with the card as one `criteria=`.
 *
 * Prerender: a bare `/results` (no card state) builds the SAME list query key
 * the SSR seed writes (`entry-server.seedCatalogMount`, awarded + the month
 * window + the outcome sort + page) —
 * `criteria` is `undefined` there and react-query drops undefined keys — so
 * the guest page still ships its rows. The count line is not seeded: it reads
 * «Считаем итоги…» in the HTML and fills in after hydration.
 *
 * The rows themselves (`TenderList`, with the outcome wall's per-row lock
 * chip) are untouched.
 */
export function ResultsCatalog() {
  // See catalog.tsx: the prerendered shell is hydrated with NO query string.
  const [searchParams, setSearchParams] = useShellSearchParams();
  const [density] = useListDensity();
  const { t, locale } = useLocale();
  const navigate = useLocaleNavigate();
  const { status: authStatus, user } = useAuth();
  const authed = authStatus === 'authed';

  const state = useMemo(() => parseResultsParams(searchParams), [searchParams]);
  const criteria = resultsCriteriaParam(state);
  const page = resolveListingPage(useParams().page, searchParams.get('page'));
  const offset = (page - 1) * PAGE_SIZE;

  useEffect(() => track('catalog_browsed', { list: 'awarded' }), []);
  usePageTitle(t('results.page.h1'));

  // The last month of outcomes, newest first (owner, 2026-10-07) — the SAME
  // three axes the SSR seed (`entry-server.seedCatalogMount`) and the
  // prerender's `/results/page/N` queries carry, or the seed would miss.
  //
  // ⚠️ The seed is the GUEST answer (winners walled), and `refetchOnMount:
  // false` keeps it — so a Pro reader opening /results by a direct link, with a
  // live session, kept seeing «в Pro» instead of winners: the cache is reset on
  // a login (`adoptSession`), not when a page loads with a session already in
  // place (review 09.10). The key therefore carries WHO is reading: a guest —
  // and everyone while `/auth/me` is still loading, i.e. at hydration — reads
  // the seed under the plain key; a signed-in reader gets his own key, so the
  // moment the session resolves the list is fetched for him. The guest list
  // of the SAME card stays on screen until then (only that transition — a chip
  // or page change still shows its skeleton), no CLS.
  const baseListKey = catalogListKey(
    {
      status: 'closed',
      awarded: true,
      outcomeDays: RESULTS_WINDOW_DAYS,
      sort: RESULTS_SORT,
      criteria,
      page,
    },
    locale,
  );
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: authed && user ? [...baseListKey, { viewer: user.id }] : baseListKey,
    placeholderData: (prev, prevQuery) =>
      prevQuery && hashKey(prevQuery.queryKey) === hashKey(baseListKey) ? prev : undefined,
    queryFn: () =>
      fetchCatalog({
        status: 'closed',
        awarded: true,
        outcomeDays: RESULTS_WINDOW_DAYS,
        sort: RESULTS_SORT,
        criteria,
        offset,
        limit: PAGE_SIZE,
        lang: locale,
      }),
    // As on the catalogue: keep the prerendered snapshot on load (CLS).
    refetchOnMount: false,
  });

  const { data: summary } = useQuery({
    queryKey: ['catalog', 'awarded-summary', criteria ?? ''],
    queryFn: () => fetchAwardedSummary(criteria),
    staleTime: 60_000,
  });

  /*
   * The reader's saved results search pre-fills a BARE /results — once per
   * visit, and only if the URL carries no card state of its own (a shared
   * link or the alert's «+N ещё» wins). After that, clearing the chips means
   * clearing them: the ref keeps the pre-fill from coming back.
   */
  const { data: saved } = useQuery({
    queryKey: ['results-search'],
    queryFn: getResultsSearch,
    enabled: authed,
  });
  // Lots searches — the same payload the catalogue's spotlight and the sidebar
  // already read (shared cache key), so no extra endpoint for the count.
  const { data: lotsSearches } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: authed,
  });
  /*
   * The highlighted card (owner, 2026-10-07): a guest, or an account with NO
   * saved search of any kind — neither a lots search nor the results one. A
   * reader who saved anything (the main onboarding saves a lots search) is not
   * nagged. 'loading' counts as a guest, so the prerender and the first client
   * render agree (no #418); a signed-in reader is spotlit only once BOTH lists
   * are known to be empty, so a returning reader with searches never sees the
   * card flash orange.
   */
  const spotlight =
    !authed || (lotsSearches !== undefined && lotsSearches.length === 0 && saved === null);
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || saved === undefined) return;
    prefilled.current = true;
    if (!saved || hasResultsState(state)) return;
    setSearchParams(
      writeResultsParams(fromResultsCriteria(saved.criteria as ResultsCriteria), searchParams),
      { replace: true },
    );
    // Only the arrival of `saved` should trigger this — see the ref.
  }, [saved]);

  function onChange(next: ResultsCardState) {
    // Through the PATH, not just the query: on `/results/page/3` the page lives
    // in the path, and a new filter must start from page 1.
    const qs = writeResultsParams(next, searchParams).toString();
    navigate(`/results${qs ? `?${qs}` : ''}`);
  }

  const hasState = hasResultsState(state);
  const pageHref = !hasState ? (n: number): string => listingPageHref('/results', n) : undefined;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const canonicalPath = pageHref ? pageHref(page) : '/results';
  const prevPath = pageHref && page > 1 ? pageHref(page - 1) : undefined;
  const nextPath = pageHref && page < totalPages ? pageHref(page + 1) : undefined;

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
  }

  // The page-level wall line: anyone who is not paying, where the wall exists.
  // Guests (and the prerender) see it; a Pro reader never does.
  const showWall = countryConfig.code === 'uz' && !isPaidUser(user) && !data?.viewerIsPro;

  return (
    <>
      <Seo
        title={t(page > 1 ? 'seo.results.titlePaged' : 'seo.results.title', { page })}
        description={t('seo.results.description')}
        canonical={canonicalPath}
        prev={prevPath}
        next={nextPath}
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: t('results.page.h1'),
          url: `${SITE_ORIGIN}${localePath('/results', locale)}`,
          numberOfItems: data?.items?.length ?? 0,
          itemListElement: (data?.items ?? []).map((item, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `${SITE_ORIGIN}${localePath(tenderPath(item.slug, item.id), locale)}`,
            name: item.title,
          })),
        }}
      />

      <h1 className="sr-only">{t('results.page.h1')}</h1>

      <OnboardingBanner />

      <p className="mb-4 text-[13px] leading-relaxed text-ink-soft md:max-w-5xl">
        {t('results.page.intro')}
      </p>

      <ResultsSearchCard
        state={state}
        onChange={onChange}
        summary={summary}
        showWall={showWall}
        spotlight={spotlight}
        // Pro «Экспорт в Excel» — the lots export's button, gate and walls
        // (signed-out: not rendered; not Pro: → /plans), on the count line as on
        // the catalogue. The winners of the last month that match the card; no
        // phones in the file (the API never puts them there).
        countTrailing={
          <ExportButton
            compact
            className="h-7 w-7 rounded-md"
            download={() => downloadResultsExport({ criteria, lang: locale })}
          />
        }
      />

      <div className="mt-4 min-h-[480px]">
        {isLoading ? (
          <>
            <SlowLoadNotice />
            <TenderListSkeleton />
          </>
        ) : isError ? (
          <QueryError onRetry={() => void refetch()} error={error} />
        ) : (
          <>
            <TenderList items={data?.items ?? []} compact={density === 'compact'} />
            {data && (
              <Pagination
                page={page}
                limit={PAGE_SIZE}
                total={data.total}
                onPageChange={goToPage}
                hrefFor={pageHref}
              />
            )}
          </>
        )}
      </div>

      <div className="mt-5">
        <ChannelSubscribeCta surface="catalog" />
        <UpgradeBanner />
      </div>
    </>
  );
}
